import {
    ChangeDetectionStrategy,
    Component,
    Input,
    ViewEncapsulation,
} from '@angular/core';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { TranslateService } from '@ngx-translate/core';
import * as React from 'react';

import iocTypes from '../rvirus.imap';
import { lazyInject } from '../rvirus.ioc';

import { TranslationKeyMap } from './insight.i18n';
import {
    LegendInsightProps,
    MetricThreshold,
    PerformanceTrend,
} from './legend.insight.model';
import { PopUpFrameInsight } from './pop-up-frame.insight.component';
import { PopUp } from './pop-up.component';

class LegendInsightImpl extends React.Component<LegendInsightProps> {
    @lazyInject(iocTypes.TranslateService.identifier)
    private readonly _translateService: TranslateService;

    private readonly _popUpRef: React.RefObject<PopUp>;

    constructor(props: LegendInsightProps) {
        super(props);

        this._popUpRef = React.createRef();
    }

    render(): React.ReactNode {
        if (
            !this.props.metricThresholds ||
            this.props.metricThresholds.length === 0
        ) {
            return null;
        }
        const legendStr = this._translateService.instant(
            TranslationKeyMap._7391_LEGEND,
        );
        const thresholdStr = this._translateService.instant(
            TranslationKeyMap._7149_THRESHOLD,
        );
        return (
            <div className="insight-legend">
                <button
                    className="btn bg-transparent insight-pop-up-open"
                    tabIndex={-1}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async, @typescript-eslint/prefer-optional-chain
                    onClick={() =>
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._popUpRef.current && this._popUpRef.current.show()
                    }
                >
                    {legendStr}
                </button>
                <PopUp
                    target={() =>
                        document.querySelector(
                            '.insight-legend .insight-pop-up-open',
                        )
                    }
                    placement="left-top"
                    triangle={true}
                    containerClass="insight-legend insight-pop-up-container"
                    backdrop={true}
                    backdropClass="insight-legend insight-pop-up-backdrop"
                    backdropDismiss={true}
                    relativeTo={() =>
                        document.querySelector('[data-insight="root"]')
                    }
                    ref={this._popUpRef}
                >
                    <PopUpFrameInsight
                        containerClass="insight-legend"
                        title={legendStr}
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        onCloseClick={() =>
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            this._popUpRef.current &&
                            this._popUpRef.current.close()
                        }
                    >
                        <div className="insight-legend insight-pop-up-content insight-scrollable">
                            <table className="bordered">
                                <thead>
                                    <tr>
                                        <th>&nbsp;</th>
                                        <th colSpan={3}>
                                            {this._translateService.instant(
                                                TranslationKeyMap._7147_NEGATIVE_PERFORMANCE,
                                            )}
                                        </th>
                                        <th colSpan={3}>
                                            {this._translateService.instant(
                                                TranslationKeyMap._7148_POSITIVE_PERFORMANCE,
                                            )}
                                        </th>
                                    </tr>
                                    <tr>
                                        <th>
                                            {this._translateService.instant(
                                                TranslationKeyMap._7146_METRIC_NAME,
                                            )}
                                        </th>
                                        <th>{thresholdStr} 3</th>
                                        <th>{thresholdStr} 2</th>
                                        <th>{thresholdStr} 1</th>
                                        <th>{thresholdStr} 1</th>
                                        <th>{thresholdStr} 2</th>
                                        <th>{thresholdStr} 3</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.metricThresholds.map(
                                        (item, key) => (
                                            <tr key={key}>
                                                <td>{item.metricShortName}</td>
                                                <td
                                                    style={{
                                                        color:
                                                            this.props
                                                                .showThresholdColours &&
                                                            item.neg3Colour,
                                                    }}
                                                >
                                                    {item.neg3Value}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            this.props
                                                                .showThresholdColours &&
                                                            item.neg2Colour,
                                                    }}
                                                >
                                                    {item.neg2Value}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            this.props
                                                                .showThresholdColours &&
                                                            item.neg1Colour,
                                                    }}
                                                >
                                                    {item.neg1Value}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            this.props
                                                                .showThresholdColours &&
                                                            item.pos1Colour,
                                                    }}
                                                >
                                                    {item.pos1Value}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            this.props
                                                                .showThresholdColours &&
                                                            item.pos2Colour,
                                                    }}
                                                >
                                                    {item.pos2Value}
                                                </td>
                                                <td
                                                    style={{
                                                        color:
                                                            this.props
                                                                .showThresholdColours &&
                                                            item.pos3Colour,
                                                    }}
                                                >
                                                    {item.pos3Value}
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                            <br />
                            {this.props.showTrendArrows && (
                                <table className="bordered">
                                    <thead>
                                        <tr className="bordered">
                                            <th>
                                                {this._translateService.instant(
                                                    TranslationKeyMap._989_TREND,
                                                )}
                                            </th>
                                            <th>
                                                {this._translateService.instant(
                                                    TranslationKeyMap._7150_IMAGE,
                                                )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.props.trends.map((item, key) => (
                                            <tr key={key}>
                                                <td>{item.trendName}</td>
                                                <td>
                                                    {item.trendClass?.split(' ')
                                                        ?.length > 1 && (
                                                        <div
                                                            style={{
                                                                color: item.trendClass.split(
                                                                    ' ',
                                                                )[0],
                                                            }}
                                                            className="d-inline cmps-icon"
                                                        >
                                                            <svg>
                                                                {item.trendClass
                                                                    .split(
                                                                        ' ',
                                                                    )[1]
                                                                    .toLowerCase()
                                                                    .includes(
                                                                        'up',
                                                                    ) && (
                                                                    <path
                                                                        d={
                                                                            mdiChevronUp
                                                                        }
                                                                    ></path>
                                                                )}
                                                                {item.trendClass
                                                                    .split(
                                                                        ' ',
                                                                    )[1]
                                                                    .toLowerCase()
                                                                    .includes(
                                                                        'down',
                                                                    ) && (
                                                                    <path
                                                                        d={
                                                                            mdiChevronDown
                                                                        }
                                                                    ></path>
                                                                )}
                                                            </svg>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </PopUpFrameInsight>
                </PopUp>
            </div>
        );
    }
}

export const LegendInsight = LegendInsightImpl;

@Component({
    selector: '[rvirus][cmpsInsightLegend]',
    template: ` <cmps-insight-legend
        [metricThresholds]="metricThresholds"
        [trends]="trends"
        [showThresholdColours]="showThresholdColours"
        [showTrendArrows]="showTrendArrows"
    ></cmps-insight-legend>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class LegendInsightComponent {
    @Input() metricThresholds: MetricThreshold[];
    @Input() trends: PerformanceTrend[];
    @Input() showThresholdColours: boolean;
    @Input() showTrendArrows: boolean;
}
